import { api } from '../axios';
import {
  MaterialResponse,
  MaterialWhere,
  SubjectsResponse,
  MaterialHistoryResponse,
  MaterialHistoryWhere,
  MaterialTree,
  MaterialTreeHistory,
  MaterialHistory,
  Material,
  headers,
  TopicTree,
  NoAuthError,
  Messages,
  FetchMaterialParams,
  Subjects,
  ContestSubject,
} from '../../types';
import Mongo from './Mongo';
import cloneDeep from 'lodash.clonedeep';
import Authentication from './Authentication';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import NotifyHelper from '../NotifyHelper';

const MATERIAL_ENDPOINT = '/material';
const HISTORY_END_POINT = '/history';

class Materials extends Mongo {
  public async fetch({
    where,
    page,
    max = 10,
  }: FetchMaterialParams): Promise<MaterialResponse> {
    const flattenQuery = this.flattenMongoQuery({ query: where }) as unknown;
    const response: MaterialResponse = await api.get(MATERIAL_ENDPOINT, {
      params: {
        page,
        max_results: max,
        where: flattenQuery,
      },
    });
    return response;
  }

  public async fetchMateiralById(id: string): Promise<Material> {
    const response: AxiosResponse<Material> = await api.get(
      `${MATERIAL_ENDPOINT}/${id}`
    );
    return response.data;
  }

  public async countMaterials({ where }: FetchMaterialParams): Promise<number> {
    const user = Authentication.loggedUser();
    if (!user) throw NoAuthError;
    if (!user.contest?._id) throw NoAuthError;
    const flattenQuery = this.flattenMongoQuery({ query: where }) as unknown;
    const axiosConfig: AxiosRequestConfig = {
      params: {
        where: flattenQuery,
      },
    };
    return api.head(MATERIAL_ENDPOINT, axiosConfig).then((response) => {
      const _headers = response.headers as headers;
      return _headers['x-total-count'];
    });
  }

  public async createMaterialHistory(
    material: MaterialHistory
  ): Promise<MaterialHistory | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      material.user_email = loginData.user.email;
      const response = await api.post(
        `${HISTORY_END_POINT}/material`,
        material
      );
      return response.data as MaterialHistory;
    } catch (error) {
      console.error(error);
    }
  }

  public async updateMaterialHistory(
    material: MaterialHistory
  ): Promise<MaterialHistory | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      material.user_email = loginData.user.email;
      const etag = material._etag;
      const id = material._id;
      const payload = cloneDeep(
        this.removeMongoAttrs(material)
      ) as MaterialHistory;
      const response = await api.put(
        `${HISTORY_END_POINT}/material/${id as string}`,
        payload,
        {
          headers: {
            'If-Match': etag,
          },
        }
      );
      NotifyHelper.notifySuccess(Messages.UPDATE_MATERIAL_HISTORY_SUCCESS);
      return response.data as MaterialHistory;
    } catch (error) {
      NotifyHelper.notifyError(Messages.UPDATE_MATERIAL_HISTORY_ERROR);
      console.error(error);
    }
  }

  public async fetchMaterialHistory({
    where,
  }: {
    where: MaterialHistoryWhere;
  }): Promise<MaterialHistoryResponse | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const flattenQuery = this.flattenMongoQuery({
        query: {
          ...where,
          user_email: loginData.user.email,
        } as unknown,
      }) as unknown;
      const response: MaterialHistoryResponse = await api.get(
        `${HISTORY_END_POINT}/material`,
        {
          params: {
            where: flattenQuery,
          },
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response;
    } catch (error) {
      console.error(error);
      NotifyHelper.notifyError(Messages.FETCH_MATERIAL_HISTORY_ERROR);
    }
  }

  public async countMaterialHistory(
    subjects: string[]
  ): Promise<number | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const flattenQuery = this.flattenMongoQuery({
        query: {
          subject: {
            $in: subjects,
          },
          user_email: loginData.user.email,
        },
      }) as unknown;
      const response = await api.head(`${HISTORY_END_POINT}/material`, {
        params: {
          where: flattenQuery,
        },
      });
      interface headers {
        'x-total-count': number;
      }
      const _headers = response.headers as headers;
      return _headers['x-total-count'];
    } catch (error) {
      console.error(error);
      NotifyHelper.notifyError(Messages.COUNT_MATERIAL_HISTORY_ERROR);
    }
  }

  public fetchSubjects(subjectsQuery?: string[]): SubjectsResponse | undefined {
    try {
      const user = Authentication.loggedUser();
      if (!user) return;
      const response = {
        data: user.contest.subjects
          .map((subject: ContestSubject) => {
            return {
              title: subject.subject,
              topics: subject.topics.map((topic) => {
                return topic.name;
              }),
              number_of_questions: subject.number_of_questions,
            };
          })
          .sort((a, b) => b.number_of_questions - a.number_of_questions),
      };
      if (subjectsQuery?.length) {
        response.data = response.data.filter((subject: { title: unknown }) =>
          subjectsQuery.includes(subject.title as string)
        );
      }
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  public async fetchMaterialBySubjects(subjectQuery?: string[]) {
    const user = Authentication.loggedUser();
    if (!user) return;

    const subjects = this.fetchSubjects(subjectQuery);
    if (!subjects) return;
    const materialHistory = await this.fetchMaterialHistory({
      where: {
        subject: {
          $in: subjects.data.map((subject) => subject.title),
        },
      },
    });
    const subjectList = subjects.data.map((subject) => subject.title);
    const materialsConfig = {
      where: {
        subject: {
          $in: subjectList,
        },
        showing: true,
        status: {
          label: 'visivel',
        },
      } as MaterialWhere,
      max: 10,
    };
    const countMaterials = await this.countMaterials({
      where: materialsConfig.where,
    });
    materialsConfig.max = countMaterials;
    const materialsResponse = await this.fetch(materialsConfig);
    return this.buildMaterialTree(materialsResponse, subjects, materialHistory);
  }

  public buildMaterialSubjects(
    materialsResponse: MaterialResponse,
    subjectQuery?: string[]
  ) {
    const subjectAndTopic = materialsResponse.data._items.map((el) => {
      return {
        title: el.subject,
        topic: el.topic,
      };
    });
    const subjectSet = new Set();
    subjectAndTopic.map((el) => el.title).map((el) => subjectSet.add(el));
    const subjectArray = Array.from(subjectSet);
    const tree: Subjects[] = [];
    subjectArray.forEach((subject) => {
      const topicSet = new Set();
      subjectAndTopic
        .filter((obj) => obj.title === subject)
        .map((el) => topicSet.add(el.topic));
      const topicArray = Array.from(topicSet);
      const subjectWithTopics = {
        title: subject as string,
        topics: topicArray as string[],
      };
      tree.push(subjectWithTopics);
    });
    const subjects: SubjectsResponse = {
      data: tree,
    };
    if (subjectQuery?.length) {
      subjects.data = subjects.data.filter((subject) =>
        subjectQuery.includes(subject.title)
      );
    }
    return subjects;
  }

  public async fetchMaterialByTag(subjectQuery?: string[]) {
    const loginData = Authentication.loggedUser();
    if (!loginData) return;
    const tags = loginData.contest.methodology_lookup.tags;
    const materialsConfig = {
      where: {
        tags: { $in: tags },
        showing: true,
        status: {
          label: 'visivel',
        },
      } as MaterialWhere,
      max: 10,
    };
    const countMaterials = await this.countMaterials({
      where: materialsConfig.where,
    });
    materialsConfig.max = countMaterials;
    const materialsResponse = await this.fetch(materialsConfig);
    const subjects: SubjectsResponse = this.buildMaterialSubjects(
      materialsResponse,
      subjectQuery
    );
    const materialHistory = await this.fetchMaterialHistory({
      where: {
        subject: {
          $in: subjects.data.map((subject) => subject.title),
        },
      },
    });
    return this.buildMaterialTree(materialsResponse, subjects, materialHistory);
  }

  public buildMaterialTree(
    materialList: MaterialResponse,
    subjects: SubjectsResponse,
    materialHistory?: MaterialHistoryResponse
  ) {
    try {
      const materials: Material[] = materialList.data._items;
      const tree: MaterialTree[] = [];
      for (const subjectItem of subjects.data) {
        const subjectTree = {
          subject: subjectItem.title,
          topics: [] as TopicTree[],
        };
        if (subjects.data.length && subjectItem.topics) {
          for (const topic of subjectItem.topics) {
            const topicTree = {
              title: topic,
              materials: [] as MaterialTreeHistory[],
            };
            const filteredMaterials = materials.filter(
              (material) =>
                material.subject === subjectItem.title &&
                material.topic === topic
            );
            for (const material of filteredMaterials) {
              const materialTree: MaterialTreeHistory = {
                _id: material._id as string,
                _created: material._created as string,
                _updated: material._updated as string,
                title: material.title,
                description: material.description,
                links: material.links,
              };

              const history = materialHistory?.data?._items;

              if (history && history.length) {
                const searchHistory = history.find(
                  (history) => history.material_id === material._id
                );
                if (searchHistory) {
                  materialTree.history = searchHistory;
                }
              }

              topicTree.materials.push(materialTree);
            }
            if (topicTree.materials.length)
              [subjectTree.topics.push(topicTree)];
          }
        }
        const getTotalMaterial = subjectTree.topics.reduce(
          (acc, topic: TopicTree) => {
            const sum = acc + topic.materials.length;
            return sum;
          },
          0
        );
        if (getTotalMaterial) {
          tree.push(subjectTree);
        }
      }
      return tree;
    } catch (error) {
      console.error(error);
    }
  }
}

export default new Materials();
